import Logo from "@/ui/components/Logo";
import ThinkingAnimation from "@/ui/components/ThinkingAnimation";
import { useState, useEffect } from "preact/hooks";
import { toTitleCase } from "@/lib/strings";
import { getLocalizationForManifest, postFeedback } from "@/lib/api_controller";
import { uiLangIsRtl } from "@/l11n/l11n";

const MAX_CONTENT_WIDTH = "360px";

const ActivityFeedback = () => {
  const [feedbackItems, setFeedbackItems] = useState({});
  const [showThanks, setShowThanks] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [l11n, setL11n] = useState({});
  const [activityId, setActivityId] = useState(null);

  const uiIsRtl = uiLangIsRtl();

  // MARK: onLoad
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // Get Activity ID From URL
    const activityIdParam = params.get("aid");
    if (activityIdParam == null) {
      console.error("No activity ID provided. Using null.");
    } else {
      setActivityId(activityIdParam);
    }

    // Fetch the localization
    async function fetchLocalization() {
      let localization = await getLocalizationForManifest(page_localization_manifest);
      if (!localization) {
        console.error("Failed to fetch localization. Using English fallback.");
        localization = fallback_localization_en;
      }
      console.log(`Localization:`, localization);
      setL11n(localization);
      setIsLoading(false);
    }
    fetchLocalization();
  }, []);

  // Process rating parameter after activityId is set
  useEffect(() => {
    if (activityId) {
      const params = new URLSearchParams(window.location.search);
      const ratingParam = params.get("overall_rating");
      if (ratingParam !== null) {
        const rating = parseInt(ratingParam, 10);
        if (!isNaN(rating) && rating >= 0 && rating <= 4) {
          console.log("setting overall_rating", rating);
          setFeedbackItem("overall_rating", {
            value: rating,
            scaleIndex: 4,
            percentage: (rating / 4) * 100,
          });
        }
      }
    }
  }, [activityId]);

  // MARK: Overall Rtg
  const ratings = [
    { level: 0, label: l11n.rating_very_bad, emoji: "😖" },
    { level: 1, label: l11n.rating_poor, emoji: "🙁" },
    { level: 2, label: l11n.rating_okay, emoji: "😐" },
    { level: 3, label: l11n.rating_good, emoji: "🙂" },
    { level: 4, label: l11n.rating_great, emoji: "😄" },
  ];

  // MARK: Length Rtg
  const lengthRatingProto = {
    id: "length",
    title: l11n.rating_length_title,
    description: l11n.rating_length_description,
    scaleIndex: 2,
    units: [
      { title: l11n.rating_too_short, value: "0" },
      { title: l11n.rating_just_right, value: "2" },
      { title: l11n.rating_too_long, value: "1" },
    ],
  };

  // MARK: Difficulty Rtg
  const difficultyRatingProto = {
    id: "difficulty",
    title: l11n.rating_difficulty_title,
    description: l11n.rating_difficulty_description,
    scaleIndex: 2,
    units: [
      { title: l11n.rating_too_easy, value: "0" },
      { title: l11n.rating_just_right, value: "2" },
      { title: l11n.rating_too_hard, value: "1" },
    ],
  };

  // MARK: Report Rtg
  const reportRatingProto = {
    id: "report",
    title: l11n.rating_report_title,
    description: l11n.rating_report_description,
    scaleIndex: 2,
    units: [
      { title: l11n.rating_not_useful, value: "0" },
      { title: l11n.rating_okay, value: "1" },
      { title: l11n.rating_very_useful, value: "2" },
    ],
  };

  // MARK: Another Activity
  const anotherActivityProto = {
    id: "another_activity",
    title: l11n.rating_another_activity_title,
    description: l11n.rating_another_activity_description,
    scaleIndex: 2,
    units: [
      { title: l11n.rating_no, value: "0", emoji: "👎" },
      { title: l11n.rating_maybe, value: "1", emoji: "🤷‍♂️" },
      { title: l11n.rating_definitely, value: "2", emoji: "👍" },
    ],
  };

  // MARK: Taalia Rtg
  const taaliaRatingProto = {
    id: "overall_taalia_experience",
    title: l11n.rating_overall_taalia_title,
    description: l11n.rating_overall_taalia_prompt,
    scaleIndex: 4,
    units: [
      { title: l11n.rating_very_bad, value: "0", emoji: "😖" },
      { title: l11n.rating_poor, value: "1", emoji: "🙁" },
      { title: l11n.rating_okay, value: "2", emoji: "😐" },
      { title: l11n.rating_good, value: "3", emoji: "🙂" },
      { title: l11n.rating_great, value: "4", emoji: "😄" },
    ],
  };

  // MARK: functions
  async function setFeedbackItem(id, value) {
    console.log("setting feedback item", id, value);
    const updatedFeedbackItems = { ...feedbackItems, [id]: value };
    setFeedbackItems(updatedFeedbackItems);
    const feedbackData = {
      type: "activity",
      activityId,
      items: updatedFeedbackItems,
    };
    console.log("posting feedback", feedbackData);
    postFeedback(feedbackData);
  }

  async function submitFeedback() {
    // Get the feedback text from the textarea
    const feedbackText = document.querySelector("textarea").value;
    if (feedbackText.length > 0) {
      setFeedbackItem("feedbackText", feedbackText);
    }

    // Show the thank you message
    setShowThanks(true);
  }

  // MARK: early renders
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <ThinkingAnimation />
      </div>
    );
  } else if (activityId == null) {
    return (
      <div
        className="text-2xl font-semibold text-gray-800 mb-4 break-words max-w-[380px] mx-auto"
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        No activity ID provided.
      </div>
    );
  }

  // MARK: render
  return (
    <>
      <div className="flex flex-col min-h-screen space-y-4">
        {/* Logo */}
        <div className="mt-8">
          <Logo />
        </div>

        {/* Overall Rating */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <OverallRatingRow
            ratings={ratings}
            feedbackItems={feedbackItems}
            l11n={l11n}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* Length Rating */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <ThreePointRow
            item={lengthRatingProto}
            selectedItem={feedbackItems.length}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* Difficulty Rating */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <ThreePointRow
            item={difficultyRatingProto}
            selectedItem={feedbackItems.difficulty}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* Report Rating */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <ThreePointRow
            item={reportRatingProto}
            selectedItem={feedbackItems.report}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* Another Activity */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <ThreePointRow
            item={anotherActivityProto}
            selectedItem={feedbackItems.another_activity}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* Taalia Rating */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <ThreePointRow
            item={taaliaRatingProto}
            selectedItem={feedbackItems.overall_taalia_experience}
            setFeedbackItemCallback={setFeedbackItem}
            uiIsRtl={uiIsRtl}
          />
        </div>

        {/* Additional Feedback */}
        <div
          className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto my-4 py-4 px-2 text-center bg-gray-50 rounded-xl`}
        >
          <AdditionalFeedbackRow l11n={l11n} uiIsRtl={uiIsRtl} />
        </div>

        {/* Submit Button */}
        <div className={`w-full max-w-[${MAX_CONTENT_WIDTH}] mx-auto mb-8 px-10`}>
          <button
            className="w-full py-3 px-4 mb-8 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-[clamp(0.875rem,2.5vw,1rem)]"
            onClick={() => submitFeedback()}
          >
            {l11n.rating_submit}
          </button>
        </div>
      </div>

      {showThanks && (
        <div className="fixed inset-0 bg-white flex items-center justify-center">
          <div className="text-[clamp(1.5rem,5vw,2rem)] font-semibold text-gray-800">
            {l11n.rating_thank_you}
          </div>
        </div>
      )}
    </>
  );
};

export default ActivityFeedback;

// MARK: Overall Rating
const OverallRatingRow = ({
  ratings,
  feedbackItems,
  l11n,
  setFeedbackItemCallback,
  uiIsRtl,
}) => {
  return (
    <>
      <div
        className={`text-[clamp(1.25rem,4vw,1.5rem)] font-semibold text-gray-800 mb-4 break-words w-full text-center px-2`}
        style={{
          direction: uiIsRtl ? "rtl" : "ltr",
        }}
      >
        {l11n.rating_prompt}
      </div>
      <div className="flex justify-center items-center m-0 p-0 gap-2">
        {ratings.map(({ level, label, emoji }) => (
          <div
            key={level}
            onClick={() =>
              setFeedbackItemCallback("overall_rating", {
                value: level,
                scaleIndex: 4,
                percentage: (level / 4) * 100,
              })
            }
            className={`flex flex-col items-center justify-center cursor-pointer p-2 rounded-lg transition-all duration-200 border border-transparent hover:border-gray-300 w-[68px] ${
              feedbackItems.overall_rating?.value === level
                ? "!bg-[#b5d8ff] scale-105"
                : "bg-gray-100"
            }`}
          >
            <div className="h-8 mb-2 flex items-center">
              <span className="text-[clamp(2rem,6vw,2.5rem)]">{emoji}</span>
            </div>
            <span className="text-[clamp(0.875rem,2.5vw,1rem)] font-semibold text-gray-600 min-h-[2.5rem] text-center w-full flex items-center justify-center">
              {toTitleCase(label)}
            </span>
          </div>
        ))}
      </div>
    </>
  );
};

// MARK: 3 Point Row
const ThreePointRow = ({ item, selectedItem, setFeedbackItemCallback, uiIsRtl }) => {
  return (
    <div className="w-full flex flex-col items-center">
      <div
        className={`text-[clamp(1.25rem,4vw,1.5rem)] font-semibold text-gray-800 mb-3 break-words w-full text-center px-2`}
        style={{
          direction: uiIsRtl ? "rtl" : "ltr",
        }}
      >
        {item.title.toUpperCase()}
      </div>
      <div
        className={`text-[clamp(1rem,3.5vw,1.25rem)] font-medium text-gray-800 mb-4 px-2 break-words w-full text-center`}
        style={{
          direction: uiIsRtl ? "rtl" : "ltr",
        }}
      >
        {item.description}
      </div>
      <div className="w-full flex justify-center items-center gap-2">
        {item.units.map((unit) => (
          <div
            key={unit.value}
            onClick={() =>
              setFeedbackItemCallback(item.id, {
                value: unit.value,
                scaleIndex: item.scaleIndex,
                percentage: (unit.value / item.scaleIndex) * 100,
              })
            }
            className={`flex flex-col items-center justify-center cursor-pointer p-2 rounded-lg transition-all duration-200 border border-transparent hover:border-gray-300 w-[100px] ${
              selectedItem?.value === unit.value
                ? "!bg-[#b5d8ff] scale-105"
                : "bg-gray-100"
            }`}
          >
            <div className="text-[clamp(0.875rem,2.5vw,1rem)] font-semibold text-gray-600 min-h-[2.5rem] text-center w-full flex items-center justify-center">
              {toTitleCase(unit.title)}
            </div>
            <div className="text-[clamp(1.5rem,4vw,1.75rem)] text-center w-full items-center justify-center">
              {unit.emoji ||
                Array(parseInt(unit.value) + 1)
                  .fill("⭐️")
                  .join("")}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// MARK: Addtl Feedback
const AdditionalFeedbackRow = ({ l11n, uiIsRtl }) => {
  return (
    <div
      style={{
        textAlign: uiIsRtl ? "right" : "left",
        direction: uiIsRtl ? "rtl" : "ltr",
      }}
    >
      <div
        className={`text-[clamp(1.25rem,4vw,1.5rem)] font-semibold text-gray-800 mb-4 break-words w-full text-center px-2`}
      >
        {l11n.rating_prompt_additional_title.toUpperCase()}
      </div>
      <div
        className={`text-[clamp(1rem,3.5vw,1.25rem)] font-normal text-gray-800 mb-4 px-2 break-words w-full text-left`}
        style={{
          textAlign: uiIsRtl ? "right" : "left",
          direction: uiIsRtl ? "rtl" : "ltr",
        }}
      >
        {l11n.rating_prompt_additional}
      </div>
      <div>
        <textarea
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-[clamp(0.875rem,2.5vw,1rem)]"
          placeholder={l11n.rating_prompt_additional_placeholder}
        ></textarea>
      </div>
    </div>
  );
};

const page_localization_manifest = [
  "rating_prompt",
  "rating_very_bad",
  "rating_poor",
  "rating_okay",
  "rating_good",
  "rating_great",
  "rating_prompt_additional_title",
  "rating_prompt_additional",
  "rating_thank_you",
  "rating_submit",
  "rating_prompt_additional_placeholder",
  "rating_length_title",
  "rating_length_description",
  "rating_difficulty_title",
  "rating_difficulty_description",
  "rating_report_title",
  "rating_report_description",
  "rating_too_short",
  "rating_just_right",
  "rating_too_long",
  "rating_too_easy",
  "rating_too_hard",
  "rating_not_useful",
  "rating_very_useful",
  "rating_another_activity_title",
  "rating_another_activity_description",
  "rating_no",
  "rating_maybe",
  "rating_definitely",
  "rating_overall_taalia_title",
  "rating_overall_taalia_prompt",
];

const fallback_localization_en = {
  rating_prompt: "How would you rate the activity you completed with Taalia?",
  rating_very_bad: "very bad",
  rating_poor: "poor",
  rating_okay: "okay",
  rating_good: "good",
  rating_great: "great",
  rating_prompt_additional:
    "Please provide any additional feedback you have about the activity. You can also ask questions and we'll get back to you.",
  rating_thank_you: "Thanks for the feedback!",
  rating_submit: "Submit",
  rating_prompt_additional_placeholder: "Type your message here...",
  rating_length_title: "LENGTH",
  rating_length_description: "How long was the activity?",
  rating_difficulty_title: "DIFFICULTY",
  rating_difficulty_description: "How difficult was the activity?",
  rating_report_title: "REPORT",
  rating_report_description: "How useful was the report?",
  rating_too_short: "Too Short",
  rating_just_right: "Just Right",
  rating_too_long: "Too Long",
  rating_too_easy: "Too Easy",
  rating_too_hard: "Too Hard",
  rating_not_useful: "Not Useful",
  rating_very_useful: "Very Useful",
  rating_another_activity_title: "ANOTHER ACTIVITY",
  rating_another_activity_description:
    "Are you interested in doing more activities like this one with Taalia?",
  rating_no: "No",
  rating_maybe: "Maybe",
  rating_definitely: "Definitely",
};
